import { useRef, useState, useCallback, useEffect } from 'react';

export function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export interface UseAudioPlayerReturn {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  togglePlay: () => Promise<void>;
  seekToRatio: (ratio: number) => void;
}

export function useAudioPlayer(src: string): UseAudioPlayerReturn {
  const audioRef = useRef<HTMLAudioElement>(null);
  const durationSetRef = useRef(false);
  // WebM files from MediaRecorder have no duration header — seeking to 1e101
  // forces the browser to scan the file and emit a real durationchange.
  const seekingForDurationRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Reset + force metadata fetch when src changes
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    durationSetRef.current = false;
    seekingForDurationRef.current = false;
    audioRef.current?.load();
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (seekingForDurationRef.current) return;
      setCurrentTime(audio.currentTime);
      if (!durationSetRef.current && isFinite(audio.duration) && audio.duration > 0) {
        durationSetRef.current = true;
        setDuration(audio.duration);
      }
    };

    const onLoaded = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        durationSetRef.current = true;
        setDuration(audio.duration);
        if (seekingForDurationRef.current) {
          seekingForDurationRef.current = false;
          audio.currentTime = 0;
        }
      } else if (audio.duration === Infinity && !seekingForDurationRef.current) {
        seekingForDurationRef.current = true;
        audio.currentTime = 1e101;
      }
    };

    const onPlay = () => setIsPlaying(true);
    // Syncs UI when another player on the page starts (browser pauses this one)
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('durationchange', onLoaded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('durationchange', onLoaded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        await audio.play();
      } else {
        audio.pause();
      }
    } catch {
      setIsPlaying(false);
    }
  }, []);

  const seekToRatio = useCallback(
    (ratio: number) => {
      const audio = audioRef.current;
      if (!audio || duration <= 0) return;
      audio.currentTime = Math.max(0, Math.min(1, ratio)) * duration;
      setCurrentTime(audio.currentTime);
    },
    [duration],
  );

  const progress = duration > 0 ? currentTime / duration : 0;

  return { audioRef, isPlaying, currentTime, duration, progress, togglePlay, seekToRatio };
}
